import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  ColumnFiltersState
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Box,
  Divider
} from '@mui/material';
import { useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import Pagination from '../shared/pagination';
import EmptyTable from '../../../icons/svg/empty-table.svg';
import { useTranslation } from 'react-i18next';
import { BenefitsSetRowItem, createPortfolioColumns } from './columns';
import { globalFilterFn } from '../../../util/filter';
import { BenefitPortfolioToolBar } from './toolbars/benefit-portfolio';
import { SortableTableHeader } from '../shared/sortable-header';
import { DEFAULT_PAGE_SIZE, scrollableTableContainerSx } from '../shared/utils';
import { BenefitEnum } from '@pxtra-client-lib-legacy/gen';
import { useStore } from '../../../../hooks/useStore';
import { useGetBenefitSetStats, useListBenefitSet } from '../../../../queries/benefit/benefitSet';
import { useListCustomBenefits } from '../../../../queries/custom';
import { usePaymentData } from '../../../../queries/company';
import { Loading } from '../../atomic';
import { useQueries } from '@tanstack/react-query';
import { apiClient } from '../../../util';
import { checkHasCompanyBenefitSets } from '../../../util/pricingModel';

interface PortfolioTableProps {
  onInfoClick: (benefitId: BenefitEnum) => void;
  onEditClick: (row: BenefitsSetRowItem) => void;
}

export const BenefitPortfolioTable = observer(
  ({ onInfoClick, onEditClick }: PortfolioTableProps) => {
    const { t } = useTranslation();
    const { companyStore } = useStore();

    const { data: paymentData, isLoading: isPaymentLoading } = usePaymentData(
      companyStore.company?.id ?? ''
    );
    const { data: benefitSetData, isLoading: isBenefitSetsLoading } = useListBenefitSet(
      companyStore.company?.id ?? '',
      paymentData?.pricing_model
    );
    const { data: benefitSetStatsData, isLoading: isBenefitSetStatsLoading } =
      useGetBenefitSetStats(companyStore.company?.id ?? '');
    const { data: customBenefitsData, isLoading: isCustomLoading } = useListCustomBenefits(
      companyStore.company?.id ?? '',
      paymentData?.pricing_model
    );

    const customBenefitIds = (customBenefitsData?.results ?? []).map((cb) => cb.id);

    const customBenefitQueries = useQueries({
      queries: customBenefitIds.map((id) => ({
        queryKey: ['customBenefit', id],
        queryFn: () => apiClient.benefits.benefitsCustomRetrieve({ id }),
        enabled: !!id
      }))
    });

    const hideBenefitSetRow = !checkHasCompanyBenefitSets(
      paymentData?.pricing_model,
      paymentData?.booked_benefits
    );

    const isLoading = [
      isBenefitSetsLoading,
      isBenefitSetStatsLoading,
      isCustomLoading,
      isPaymentLoading
    ].some(Boolean);

    const tableData = useMemo(() => {
      const benefitSettings = (paymentData?.booked_benefits ?? []).map((key) => {
        return {
          id: key,
          name: t(`benefits:${key}`),
          custom: false,
          entitledForBenefitCount: benefitSetStatsData?.[key]?.entitled_employees ?? 0,
          benefitInBenefitSetCount: benefitSetStatsData?.[key]?.benefit_sets ?? 0,
          activeUsageCount: benefitSetStatsData?.[key]?.active_employees ?? 0
        } as BenefitsSetRowItem;
      });

      const customBenefitSettings =
        customBenefitsData?.results?.map((cb, index) => {
          const customBenefit = customBenefitQueries[index]?.data;

          return {
            id: cb.id,
            name: cb.name ?? '',
            icon: cb.icon,
            custom: true,
            entitledForBenefitCount: customBenefit?.employees?.length,
            benefitInBenefitSetCount: 0,
            activeUsageCount: 0
          } as BenefitsSetRowItem;
        }) ?? [];

      return [...benefitSettings, ...customBenefitSettings];
    }, [paymentData, benefitSetData, benefitSetStatsData, customBenefitsData, t]);

    const [globalFilter, setGlobalFilter] = useState('');
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

    const [pagination, setPagination] = useState({
      pageIndex: 0,
      pageSize: DEFAULT_PAGE_SIZE
    });

    const columns = createPortfolioColumns(onInfoClick, onEditClick, benefitSetData?.results ?? []);

    const table = useReactTable({
      data: tableData,
      columns,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      globalFilterFn,
      onGlobalFilterChange: setGlobalFilter,
      getFacetedRowModel: getFacetedRowModel(),
      getFacetedUniqueValues: getFacetedUniqueValues(),
      getPaginationRowModel: getPaginationRowModel(),
      onPaginationChange: setPagination,
      onColumnFiltersChange: setColumnFilters,
      sortDescFirst: false,
      state: {
        pagination,
        globalFilter,
        columnFilters,
        columnVisibility: {
          benefitInBenefitSetCount: !hideBenefitSetRow
        }
      }
    });

    if (isLoading) {
      return (
        <Paper className="p-8">
          <Box className="flex justify-center items-center">
            <Loading />
          </Box>
        </Paper>
      );
    }

    return (
      <Paper className="overflow-hidden flex flex-col h-[calc(100vh-250px)]">
        <BenefitPortfolioToolBar table={table} customHeadline={'common:BENEFIT'} />
        <TableContainer sx={scrollableTableContainerSx('calc(100vh - 400px)')}>
          <Table>
            <TableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <SortableTableHeader key={header.id} header={header} />
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {table.getRowModel().rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="!border-none">
                    <div className="flex flex-col items-center justify-center py-8">
                      <img
                        src={EmptyTable}
                        alt={t('common:EMPTY_TABLE_TITLE_rows')}
                        className="w-64 h-64"
                      />
                      <p className="mt-4 text-grey-500">{t('common:EMPTY_TABLE_TITLE_rows')}</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell className="py-2 border-grey-100" key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Divider className="border-grey-100" />
        <Pagination table={table} />
      </Paper>
    );
  }
);
